import Controller from '@ember/controller'
import type { ControllerQueryParam } from '@ember/controller'
import { all } from 'rsvp'
import { service } from '@ember/service'
import { isEmpty } from '@ember/utils'
import { capitalize } from '@ember/string'
import { action } from '@ember/object'
import { task } from 'ember-concurrency'
import config from 'teacher-ui-client/config/environment'
import { isConflictError } from 'teacher-ui-client/resource-conflict/error'
import { possessiveName } from 'teacher-ui-client/utils/name'
import { join } from 'teacher-ui-client/utils/uri'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { TABLE_THEMES } from 'teacher-ui-client/manage/table-themes'
import { subscriptionTypeToThemeKey } from 'teacher-ui-client/utils/themes'
import { buildDataOperationsConfigProxy } from 'teacher-ui-client/components/data-operations/data-operations-proxy'
import { SortType, SortDirection } from 'teacher-ui-client/components/data-operations/table/sort-definition'
import { tracked } from '@glimmer/tracking'
import type Teacher from 'teacher-ui-client/models/teacher'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import type { IntlService } from 'ember-intl'
import type TeacherService from 'teacher-ui-client/services/teacher'
import type DownloadFileService from 'teacher-ui-client/services/download-file'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type ResourceConflictService from 'teacher-ui-client/services/resource-conflict'
import type { Log } from '@blakeelearning/log'
import type RouterService from '@ember/routing/router-service'
import type Store from '@ember-data/store'
import type ManageTeachersRoute from 'teacher-ui-client/manage/teachers/route'
import type School from 'teacher-ui-client/models/school'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import type Student from 'teacher-ui-client/models/student'
import type TeacherSubscription from 'teacher-ui-client/models/teacher-subscription'
import { hasId } from 'teacher-ui-client/utils/type-guards'

export interface TeacherViewModel {
  id: string
  firstName: string
  lastName: string
  fullName: string
  login: string
  studentCount: number
  schoolClassNames: string
  trialSub?: TeacherSubscription
  trialEndDate?: Date
  relevantStudents: Student[] // students filtered by subtype
  teacherRecord: Teacher
}

function isDeletableTeacher(teacher: Teacher, currentTeacher: Teacher, subscriptionType: SubscriptionType) {
  // dont allow them to delete themselves
  if (teacher.id === currentTeacher.id) return false
  return teacher.schoolClasses.every((schoolClass) => schoolClass.subscriptionType === subscriptionType)
}

function isTrialExistsError(error: unknown) {
  const isObject = typeof error === 'object' && error !== null
  return (
    isObject &&
    'errors' in error &&
    Array.isArray(error.errors) &&
    error.errors
      .map((err) => {
        if (err !== null && typeof err === 'object' && 'detail' in err) {
          return err.detail
        }
        return ''
      })
      .join(' ')
      .includes('has trial already')
  )
}

const iconColorClass: Record<SubscriptionType, string> = {
  [SubscriptionType.Reading]: 'text-oceany-blue-300',
  [SubscriptionType.Maths]: 'text-ms-green-300',
  [SubscriptionType.Writing]: 'text-wl-blue-300',
}

export default class ManageTeachersController extends Controller {
  @service authToken!: AuthToken

  @service intl!: IntlService

  @service teacher!: TeacherService

  @service downloadFile!: DownloadFileService

  @service flashQueue!: FlashQueueService

  @service resourceConflict!: ResourceConflictService

  @service log!: Log

  @service router!: RouterService

  @service store!: Store

  declare model: ModelFor<ManageTeachersRoute>

  queryParams: ControllerQueryParam[] = [
    { sortCol: 'col' },
    { sortDir: 'dir' },
    { sortCompare: 'compare' },
    { currentPage: 'pageNumber' },
    { textFilter: 'filter' },
    { selectedIds: 'selected' },
    { recordsPerPage: 'perPage' },
  ]

  @tracked sortCol = 'lastName'

  @tracked sortDir = SortDirection.Asc

  @tracked sortCompare = SortType.ByValue

  @tracked currentPage = 1

  @tracked textFilter = ''

  @tracked selectedIds: string[] = []

  @tracked
  isShowingSelectTeachers = false

  @tracked
  failureCount = 0

  @tracked recordsPerPage = 10

  get editingRecordId() {
    return this.router.currentRoute?.params?.teacher_id
  }

  get dataOperationsConfig() {
    return buildDataOperationsConfigProxy(this)
  }

  get tableTheme() {
    return TABLE_THEMES[subscriptionTypeToThemeKey(this.model.subscriptionType)]
  }

  get iconColorClass(): string {
    return iconColorClass[this.model.subscriptionType] ?? ''
  }

  get teachers() {
    return this.model.teachers.filter(hasId)
  }

  get teachersWhoCanBeDeleted() {
    return this.selectedTeachers.filter((teacher) =>
      isDeletableTeacher(teacher, this.model.currentTeacher, this.model.subscriptionType),
    )
  }

  get teachersWhoCantBeDeleted() {
    return this.selectedTeachers.filter(
      (teacher) => !isDeletableTeacher(teacher, this.model.currentTeacher, this.model.subscriptionType),
    )
  }

  /**
   *  Property for the currently selected teachers.
   *  We convert all ids to strings to compare because we can't be
   *  sure of their types.
   */
  get selectedTeachers() {
    const selectedIds = (this.selectedIds === undefined ? [] : this.selectedIds).map(String)
    return this.teachers.filter((teacher) => selectedIds.includes(teacher.id))
  }

  get teachersForTable(): TeacherViewModel[] {
    // find all teachers with school-classes
    const { teachers } = this
    const filteredTeachers = teachers.filter((teacher) =>
      teacher.hasSchoolClassForSubscriptionType(this.model.subscriptionType),
    )

    // find all teacher which dont have school-classes
    const noSchoolClassTeachers = teachers.filter((teacher) => teacher.hasNoSchoolClasses)

    return [...noSchoolClassTeachers, ...filteredTeachers].map((teacher) => {
      const trialSub = teacher.latestTeacherSubscriptionFor(this.model.subscriptionType)
      const relevantStudents = teacher.studentsForSubscriptionType(this.model.subscriptionType)
      const relevantClasses = teacher.schoolClassesForSubscriptionType(this.model.subscriptionType)
      return {
        id: teacher.id,
        firstName: teacher.firstName,
        lastName: teacher.lastName,
        fullName: teacher.fullName,
        login: teacher.login,
        studentCount: relevantStudents.length,
        schoolClassNames: relevantClasses.map((sc) => sc.name).join(', '),
        trialSub,
        trialEndDate: trialSub?.endDate,
        relevantStudents,
        teacherRecord: teacher,
      }
    })
  }

  get teachersWithoutSchoolClass(): Teacher[] {
    return this.model.teachers.filter(
      (teacher) => teacher.schoolClassesForSubscriptionType(this.model.subscriptionType).length === 0,
    )
  }

  // Filter by text
  get recordsFilteredByText() {
    const data = this.teachersForTable
    const textFilter = this.textFilter || ''
    const trimmedTextFilter = textFilter.trim()
    // if no filters are applied, return all data
    if (isEmpty(trimmedTextFilter)) return data

    return data.filter((item) => {
      // if it's selected, keep it even if it doesn't match the filter
      if (this.selectedIds.includes(item.id)) return true

      const content = [item.firstName, item.lastName, item.login, item.schoolClassNames].join(' ')

      return content.toLowerCase().includes(trimmedTextFilter.toLowerCase())
    })
  }

  // Contains the results of all filtering operations
  get data() {
    return this.recordsFilteredByText
  }

  // True if all of the selected teachers (if any) have no students
  get noStudentsSelected() {
    return this.selectedTeachers.every(
      (teacher) => teacher.studentsForSubscriptionType(this.model.subscriptionType).length === 0,
    )
  }

  _createSchoolClass(id: string, store: Store, school: School, subscriptionType: SubscriptionType) {
    const teacher = store.peekRecord<Teacher>('teacher', id)
    if (!teacher) throw new Error(`teacher with id ${id} not found in local data store`)
    const teacherName = possessiveName(teacher.firstName, teacher.lastName)
    const className = this.intl.t('manage.manageTeachers.defaultClassName', {
      name: teacherName,
      subscriptionType: capitalize(subscriptionType),
    })

    return store
      .createRecord<SchoolClass>('school-class', {
        name: className,
        subscriptionType,
        teachers: [teacher],
        school,
        gamesAccess: true,
        playroomAccess: true,
        backgroundMusicDisabled: false,
        mathseedsPrimeAccess: subscriptionType === SubscriptionType.Maths,
      })
      .save()
  }

  _addTeachersToSchoolClass(teacherIds: string[]): Promise<SchoolClass | void>[] {
    const promises = teacherIds.map((id) => {
      return this._createSchoolClass(id, this.store, this.model.school, this.model.subscriptionType).catch((error) => {
        if (isConflictError(error)) {
          return this.resourceConflict.handleError('create school class', error)
        }
        this.failureCount += 1
      })
    })
    return promises
  }

  _createTeacherSubscriptions(teacherIds: string[]) {
    const promises = teacherIds.map((id) => {
      const teacher = this.store.peekRecord<Teacher>('teacher', id)
      if (!teacher) return
      return this.store
        .createRecord<TeacherSubscription>('teacher-subscription', {
          subscriptionType: this.model.subscriptionType,
          teacher,
        })
        .save()
    })
    return promises
  }

  _showSuccessMessage(count: number, type: 'trial' | 'existing' | 'emailPasswordReset') {
    const successMessage = this.intl.t(`manage.teachers.flashMessage.success.${type}`, { count })
    this.flashQueue.addSuccess({ subtitle: successMessage })
  }

  _showFailureMessage(count: number) {
    const failureMessage = this.intl.t('manage.teachers.flashMessage.failure', { count })
    this.flashQueue.addFail({ subtitle: failureMessage })
  }

  _checkForSuccess(teacherCount: number, type: 'trial' | 'existing' | 'emailPasswordReset') {
    if (this.failureCount) {
      this._showFailureMessage(this.failureCount)
    }
    if (teacherCount > this.failureCount) {
      const successCount = teacherCount - this.failureCount
      this._showSuccessMessage(successCount, type)
    }
    this.failureCount = 0
  }

  /**
   * Modals
   */
  @tracked
  addToClassModalOpen = false

  @tracked
  editPasswordModalOpen = false

  @tracked
  deleteTeachersOpen = false

  emailPasswordReset = task(
    { drop: true },
    /**
     * @param {Array<String>} selectedTeacherIds the ids of teachers to email.
     * @returns {Array<String>} the ids of teachers to email.
     */
    async (selectedTeacherIds: string[]) => {
      const url = join(config.gravityV3Url, 'teacher-password-emails').toString()
      const teachers = selectedTeacherIds.map((id) => ({
        id,
        type: 'teachers',
      }))

      try {
        const response = await fetch(url, {
          method: 'POST',
          body: JSON.stringify({ data: teachers, 'subscription-type': this.model.subscriptionType }),
          headers: {
            Authorization: this.authToken?.token ?? '',
            'content-type': 'application/vnd.api+json',
          },
        })
        if (!response.ok) throw new Error(await response.text())
        this._showSuccessMessage(selectedTeacherIds?.length, 'emailPasswordReset')
      } catch (error) {
        void this.onEmailPasswordResetError(error)
      }
    },
  )

  onEmailPasswordResetError(error: unknown) {
    this.log.error('failed to request email password reset', error ?? undefined)
    const count = this.selectedIds?.length
    const messageKey = 'manage.teachers.bulkActions.emailPasswordReset.error'
    const failureMessage = this.intl.t(messageKey, { count })
    this.flashQueue.addFail({ subtitle: failureMessage })
  }

  /**
   * Checks if all teachers has students (students is a computed prop which counts all students
   * of the teachers school classes)
   *
   */
  get selectedTeachersHaveStudents() {
    return this.selectedTeachers.every(
      (teacher) => teacher.studentsForSubscriptionType(this.model.subscriptionType).length > 0,
    )
  }

  parentLettersTask = task({ drop: true }, async () => {
    try {
      await this.downloadFile.downloadParentLettersForTeachers(this.selectedIds, this.model.subscriptionType)
      if (this.selectedTeachersHaveStudents) {
        const successMessage = this.intl.t('manage.parentLettersDownloaded.success')
        this.flashQueue.addSuccess({ subtitle: successMessage })
      } else {
        const partialSuccessMessage = this.intl.t('manage.parentLettersDownloaded.partialSuccess')
        this.flashQueue.addSuccess({ subtitle: partialSuccessMessage })
      }
    } catch {
      const message = this.intl.t('manage.parentLettersDownloaded.error')
      this.flashQueue.addFail({ subtitle: message })
    }
  })

  /**
   * Set student ids in localStorage, then open the login-cards page in a new window / tab. That page will read the ids
   * from localStorage and render the login cards in a print-optimized style.
   *
   * We use localStorage (persisted until it is cleared) rather than sessionStorage (stored until tab is closed) because
   * we need to access it from the newly opened window / tab
   */
  @action
  printStudentLoginsForTeachers(teachers: TeacherViewModel[]) {
    // Obtain a list of students for given teachers, for current sub type
    const allStudentIds = teachers.flatMap((t) => t.relevantStudents.map((student) => student.id))
    // Ensure only unique students
    const studentIds = Array.from(new Set(allStudentIds)).join(',')

    try {
      // can happen on Apple browsers where this permission is disabled
      if (!window.localStorage) throw new DOMException('localStorage is falsey', 'SecurityError')

      window.localStorage.setItem('teacher-ui-client.login-cards.student-ids', studentIds)

      const url = this.router.urlFor('login-cards', { queryParams: { subscriptionType: this.model.subscriptionType } })
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (error) {
      this.flashQueue.addFail({ subtitle: this.intl.t('manage.teachers.bulkActions.printLogins.error') })
      if (error instanceof DOMException && error.name === 'SecurityError') {
        // user may have configured browser to prevent the page from persisting data. Don't report these.
      } else {
        this.log.error(
          'unable to set value in local storage for student login cards (via manage teachers)',
          error ?? '',
        )
      }
    }
  }

  /**
   * Kicks off the parsing of a File w/csv content selected via the add-teacher-to-school component and hands over
   * control to the manage.import.teachers route.
   */
  @action
  async selectCsvFile(file: File) {
    try {
      await this.teacher.importFromCsv.perform(this.model.subscriptionType, file)
    } catch {
      // do nothing, unknown errors have been logged by the task, and the import teachers page handles task.isError
    }
    void this.router.transitionTo('manage.import.teachers')
  }

  @action
  addExisting() {
    this.isShowingSelectTeachers = true
  }

  /**
   * Add a SchoolClass and a Trial Subscription to existing users
   */
  @action
  async addExistingWithTrial(teacherIds: string[]) {
    const teacherCount = teacherIds.length
    try {
      const addTeachersPromise = all(this._addTeachersToSchoolClass(teacherIds))
      const createTeacherSubsPromise = all(this._createTeacherSubscriptions(teacherIds))
      await all([addTeachersPromise, createTeacherSubsPromise])
    } catch (error: unknown) {
      if (isConflictError(error)) {
        return this.resourceConflict.handleError('create teacher subscription(s)', error)
      }
      if (isTrialExistsError(error)) {
        // Already having a trial is fine, so do nothing
      } else {
        this.log.error('adding a trial subscription failed', error === null ? 'null' : error)
      }
    } finally {
      this.isShowingSelectTeachers = false
      this._checkForSuccess(teacherCount, 'trial')
    }
  }

  @action
  async addExistingWithSub(teacherIds: string[]) {
    await all(this._addTeachersToSchoolClass(teacherIds))
    const teacherCount = teacherIds.length

    this.isShowingSelectTeachers = false
    this._checkForSuccess(teacherCount, 'existing')
  }

  @action
  async submitDeleteTeachersModal(teachersToDelete: Teacher[]) {
    try {
      const promises = teachersToDelete.map((teacher) => teacher.destroyRecord({ adapterOptions: { bulk: true } }))
      await Promise.all(promises)
    } catch (error) {
      if (isConflictError(error)) {
        return this.resourceConflict.handleError('delete-teachers', error)
      } else {
        throw error
      }
    }
  }

  @action
  unselectAllTeachers() {
    void this.router.transitionTo('manage.teachers')
    this.selectedIds = []
  }

  @action
  setTextFilter(textFilter: string) {
    this.textFilter = textFilter
    this.currentPage = 1
  }

  @action
  showDeleteTeachersModal(open: boolean) {
    this.deleteTeachersOpen = open
  }

  @action
  showAddExistingTeachersModal(open: boolean) {
    this.isShowingSelectTeachers = open
  }
}
